// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// ── Engine definitions — add new engines here ──────────────────
export const ENGINES = [
  {
    id: 'roblox',
    label: 'Roblox Studio',
    icon: '🧱',
    color: '#00B4D8',
    desc: 'Lua scripting • Roblox marketplace • Kids & teens audience',
    badge: 'Most Popular',
    badgeColor: '#10b981',
  },
  {
    id: 'unity',
    label: 'Unity',
    icon: '⚙️',
    color: '#7C3AED',
    desc: 'C# scripting • PC, console & mobile • Asset Store integration',
    badge: 'Pro',
    badgeColor: '#7C3AED',
  },
  {
    id: 'godot',
    label: 'Godot Engine',
    icon: '🚀',
    color: '#3b82f6',
    desc: 'GDScript / C# • Open source • High-performance 2D & 3D',
    badge: 'Open Source',
    badgeColor: '#3b82f6',
  },
  {
    id: 'unreal',
    label: 'Unreal Engine 5',
    icon: '💎',
    color: '#C9A84C',
    desc: 'Blueprints / C++ • AAA quality • Nanite & Lumen rendering',
    badge: 'High Performance',
    badgeColor: '#C9A84C',
  },
];

export function getEngine(id) {
  return ENGINES.find(e => e.id === id) || ENGINES[0];
}

// Engine stored in sessionStorage so it persists across page navigations
export function getActiveEngine() {
  return sessionStorage.getItem('forgestudio_engine') || 'roblox';
}
export function setActiveEngine(id) {
  sessionStorage.setItem('forgestudio_engine', id);
}

const styles = {
  header: {
    background: 'linear-gradient(135deg, #0a0e1a 0%, #151b30 100%)',
    borderBottom: '2px solid rgba(0, 180, 216, 0.2)',
    padding: '0 1.5rem',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backdropFilter: 'blur(12px)',
  },
  inner: {
    maxWidth: 1280,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 64,
    gap: 16,
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    flexShrink: 0,
  },
  logoText: {
    fontSize: '1.25rem',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #a78bfa, #60a5fa)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.02em',
  },
  engineSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    padding: '4px 6px',
  },
  engineBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 7,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#94a3b8',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  navLink: {
    padding: '6px 14px',
    borderRadius: 8,
    fontSize: '0.85rem',
    fontWeight: 600,
    textDecoration: 'none',
    color: '#8892a6',
    transition: 'all 0.2s ease',
  },
  navLinkActive: {
    color: '#a78bfa',
    background: 'rgba(167,139,250,0.12)',
  },
};

export default function Header({ engine, onEngineChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const activeId = engine || getActiveEngine();

  const isActive = (route) => {
    if (route === '/') return path === '/';
    return path.startsWith(route);
  };

  const linkStyle = (route) => ({
    ...styles.navLink,
    ...(isActive(route) ? styles.navLinkActive : {}),
  });

  const handleEngineChange = (id) => {
    setActiveEngine(id);
    if (onEngineChange) onEngineChange(id);
    // Navigate back to home when switching engines
    if (path !== '/') navigate('/');
  };

  return (
    <header style={styles.header}>
      <div style={styles.inner}>
        {/* Logo */}
        <Link to="/" style={styles.logoLink}>
          <span style={{ fontSize: '1.5rem' }}>🎮</span>
          <span style={styles.logoText}>ForgeStudio</span>
          <span style={{
            fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px',
            borderRadius: 4, background: 'linear-gradient(135deg,#a78bfa,#60a5fa)',
            color: '#fff', marginLeft: 2, textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>Beta</span>
        </Link>

        {/* Engine Selector */}
        <div style={styles.engineSelector}>
          <span style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, paddingLeft: 4, paddingRight: 4 }}>Engine:</span>
          {ENGINES.map(eng => {
            const isSelected = activeId === eng.id;
            return (
              <button
                key={eng.id}
                style={{
                  ...styles.engineBtn,
                  color: isSelected ? eng.color : '#94a3b8',
                  background: isSelected ? `rgba(${eng.color === '#00B4D8' ? '0,180,216' : eng.color === '#7C3AED' ? '124,58,237' : eng.color === '#3b82f6' ? '59,130,246' : '201,168,76'},0.15)` : 'transparent',
                  border: isSelected ? `1px solid ${eng.color}40` : '1px solid transparent',
                }}
                onClick={() => handleEngineChange(eng.id)}
                title={eng.desc}
              >
                <span>{eng.icon}</span>
                <span style={{ display: 'none' }}>{eng.label}</span>
                <span style={{ fontSize: '0.78rem' }}>{eng.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          <Link to="/" style={linkStyle('/')}>Home</Link>
          {path.startsWith('/build') && (
            <Link to={path} style={linkStyle('/build')}>Builder</Link>
          )}
          {path === '/preview' && (
            <Link to="/preview" style={linkStyle('/preview')}>Preview</Link>
          )}
          <Link to="/pricing" style={linkStyle('/pricing')}>Pricing</Link>
        </nav>
      </div>
    </header>
  );
}
